
import React, { useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import moment from 'moment';
import { AuthContext } from '../contexts/auth';
import { estraiMessaggiErrore } from '../services/utils';
import FirmeStatusBadge from './firme_status_badge';
import FirmeSignersDialog from './firme_signers_dialog';
import FilePreviewModal from './preview_modal';
import Empty from './empty';
import CustomTooltipComponent from './custom_tooltip_component';
import {
  signedDocStatus, getSignedDoc, deleteSignedDoc, firmoDeleteSignedDoc,
  resendSignedDocInvites, refreshSignedDocStatus, isSentToProvider,
} from '../services/api_firme';
import {
  ChevronDownIcon, ChevronUpIcon, EyeIcon, DocumentCheckIcon,
  ArrowPathIcon, TrashIcon, UsersIcon,
  PaperAirplaneIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { getDocumentRecipientsDefault } from '../services/api';
import { buildDefaultEmailCsv, formatEmails } from '../services/utils_email';

const BTN_BASE = 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white';
const BTN_DANGER = 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white';

const FS_IN_PROGRESS = ['READYTOSIGN', 'IN_PROGRESS'];

const toast = (icon, titleText) => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    titleText,
    timer: 3500,
    showConfirmButton: false,
  });
};

const formatDate = (value, withTime = false) => {
  if (!value) return '—';

  const date = moment(value);

  if (!date.isValid()) return '—';

  return date.format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');
};

const formatFileSize = (bytes) => {
  if (bytes == null || !Number.isFinite(Number(bytes))) return '—';
  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

function DetailField({ label, children }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2.5 sm:grid-cols-12 sm:gap-x-4">
      <dt className="font-semibold text-gray-600 sm:col-span-4">{label}</dt>
      <dd className="min-w-0 break-words text-gray-800 sm:col-span-8">{children}</dd>
    </div>
  );
}

export default function FirmeDocsTable({
  docs = [],
  onChanged,
  onEditDeadline,
}) {
  const { token } = useContext(AuthContext);
  const [busyId, setBusyId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [signersDoc, setSignersDoc] = useState(null);
  const [openDocId, setOpenDocId] = useState(null);
  const [defaultsByEmployee, setDefaultsByEmployee] = useState({});
  const [loadingDefaultsId, setLoadingDefaultsId] = useState(null);
  const [defaultsErrorId, setDefaultsErrorId] = useState(null);
  const defaultsCache = useRef(new Map());

  const showDeadlineEdit = typeof onEditDeadline === 'function';
  const actionButtonClass = BTN_BASE;
  const dangerButtonClass = BTN_DANGER;
  const columnCount = 6;
  const toggleDoc = (id) => setOpenDocId(prev => prev === id ? null : id);

  /* SINCRONIZZAZIONE AUTOMATICA DOCUMENTI ALL'APERTURA */
  const autoSyncDone = useRef(new Set());
  const onChangedRef = useRef(onChanged);

  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  useEffect(() => {
    if (!token || !docs.length) return;

    const pendingDocs = docs.filter(doc =>
      !doc.is_signed &&
      FS_IN_PROGRESS.includes(doc.status) &&
      isSentToProvider(doc) &&
      !autoSyncDone.current.has(doc.id)
    );

    if (!pendingDocs.length) return;

    // Segna i documenti per evitare richieste ripetute
    pendingDocs.forEach(doc => autoSyncDone.current.add(doc.id));
    let cancelled = false;
    const syncDocuments = async () => {
      let changed = false;

      // Sincronizzazione in sequenza per evitare troppe richieste simultanee
      for (const doc of pendingDocs) {
        if (cancelled) return;
        try {
          const result = await refreshSignedDocStatus(token, doc.id);
          if (cancelled) return;

          if (result.status !== doc.status || result.is_signed !== doc.is_signed) {
            changed = true;
          }
        } catch (error) {
          console.error('Errore sincronizzazione automatica documento:', doc.id, error);
        }
      }

      if (!cancelled && changed) {
        onChangedRef.current?.();
      }
    };

    syncDocuments();

    return () => {
      cancelled = true;
    };
  }, [token, docs]);

  /* RECUPERO DESTINATARI PREDEFINITI */
  useEffect(() => {
    if (!openDocId || !token) return;

    const doc = docs.find(d => d.id === openDocId);
    if (!doc?.due_date) return;

    const currentEmployeeId = doc.employees?.[0]?.id ?? null;

    if (!currentEmployeeId) return;
    if (defaultsCache.current.has(currentEmployeeId)) return;

    let cancelled = false;

    const fetchDefaults = async () => {
      setLoadingDefaultsId(currentEmployeeId);
      setDefaultsErrorId(null);

      try {
        const data = await getDocumentRecipientsDefault(
          token,
          currentEmployeeId
        );

        if (cancelled) return;

        defaultsCache.current.set(currentEmployeeId, data);
        setDefaultsByEmployee(prev => ({
          ...prev,
          [currentEmployeeId]: data,
        }));
      } catch (error) {
        if (cancelled) return;

        console.error(
          'Errore caricamento destinatari default:',
          error
        );

        setDefaultsErrorId(currentEmployeeId);
      } finally {
        if (!cancelled) {
          setLoadingDefaultsId(null);
        }
      }
    };

    fetchDefaults();

    return () => {
      cancelled = true;
    };
  }, [openDocId, docs, token]);

  const openFile = async (doc, kind, attachment = null) => {
    setBusyId(doc.id);
    try {
      let url = null;
      let name = doc.name;

      if (kind === 'audit' || kind === 'acceptance') {
        url = attachment?.file_url;
        name = kind === 'audit'
          ? `${doc.name} - Audit della firma`
          : `${doc.name} - Documento di accettazione`;
      } else {
        const detail = await getSignedDoc(token, doc.id);

        url = kind === 'signed'
          ? detail.signed_file_url
          : detail.original_file_url;

        name = kind === 'signed'
          ? `${doc.name} (firmato)`
          : doc.name;
      }

      if (!url) {
        Swal.fire({
          icon: 'error',
          title: 'File non disponibile',
          text: 'Il file richiesto non è al momento disponibile.',
        });
        return;
      }

      setPreviewDoc({
        url,
        name,
        mime: 'application/pdf',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: estraiMessaggiErrore(error),
      });
    } finally {
      setBusyId(null);
    }
  };

  const sync = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await refreshSignedDocStatus(token, doc.id);
      const remote = res?.provider_status?.status;
      if (res?.is_signed || res?.status === 'COMPLETED') toast('success', 'Il documento risulta firmato');
      else if (FS_IN_PROGRESS.includes(remote)) toast('info', 'Nessuna variazione: il documento è ancora in attesa di firma');
      else if (remote === 'EXPIRED') toast('warning', 'Il documento risulta scaduto sul servizio di firma');
      else if (remote) toast('info', `Stato sul servizio di firma: ${remote}`);
      else toast('info', 'Stato non disponibile dal servizio di firma');
      onChanged?.();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Errore', text: estraiMessaggiErrore(error) });
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (doc) => {
    const result = await Swal.fire({
      icon: 'question', title: 'Reinviare le email?',
      text: "I firmatari che non hanno ancora firmato riceveranno di nuovo l'email.",
      showCancelButton: true, confirmButtonText: 'Reinvia email', cancelButtonText: 'Annulla',
    });
    if (!result.isConfirmed) return;
    setBusyId(doc.id);
    try {
      const res = await resendSignedDocInvites(token, doc.id);
      const n = res?.invites_sent;
      toast('success', typeof n === 'number' ? `Email inviate: ${n}` : 'Email reinviate');
      onChanged?.();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Errore', text: estraiMessaggiErrore(error) });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (doc) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Eliminare il documento?',
      text: doc.is_signed
        ? 'Il documento firmato verrà eliminato definitivamente.'
        : 'La richiesta di firma verrà annullata e il documento eliminato.',
      showCancelButton: true, confirmButtonText: 'Elimina',
      cancelButtonText: 'Annulla', confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    setBusyId(doc.id);
    try {
      if (!doc.is_signed && isSentToProvider(doc)) await firmoDeleteSignedDoc(token, doc.id);
      await deleteSignedDoc(token, doc.id);
      if (openDocId === doc.id) setOpenDocId(null);
      onChanged?.();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Impossibile eliminare', text: estraiMessaggiErrore(error) });
    } finally {
      setBusyId(null);
    }
  };

  /* STATO COMPLESSIVO DOCUMENTO */
  const renderDocumentStatus = doc => {
    return <FirmeStatusBadge doc={doc} />;
  };

  if (!docs.length) return <Empty />;

  return (
    <>
      {/* TABELLA RESPONSIVE */}
      <div className="w-full min-w-0 rounded-lg border border-gray-300 bg-white shadow-sm xl:max-h-[100vh] xl:overflow-y-auto">
        <table className="block w-full min-w-0 xl:table xl:table-fixed">
          <colgroup className="hidden xl:table-column-group">
            <col className="w-[4%]" />
            <col className="w-[31%]" />
            <col className="w-[15%]" />
            <col className="w-[15%]" />
            <col className="w-[15%]" />
            <col className="w-[20%]" />
          </colgroup>

          <thead className="hidden border-b border-gray-200 bg-gray-50 xl:table-header-group">
            <tr>
              <th scope="col" className="px-2 py-3"><span className="sr-only">Espandi dettagli</span></th>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-indigo-800">
                Nome
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Creato il
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Scadenza
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Stato
              </th>
              <th scope="col" className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-indigo-800">
                Azioni
              </th>
            </tr>
          </thead>

          <tbody className="block space-y-3 bg-gray-50 p-3 xl:table-row-group xl:divide-y xl:divide-gray-200 xl:space-y-0 xl:bg-white xl:p-0">
            {docs.map(doc => {
              const status = signedDocStatus(doc);
              const isPending = status === 'pending';
              const busy = busyId === doc.id;
              const expanded = openDocId === doc.id;

              const currentEmployeeId = doc.employees?.[0]?.id ?? null;
              const defaults = currentEmployeeId != null ? defaultsByEmployee[currentEmployeeId] ?? null : null;
              const loadingDefaults = currentEmployeeId != null && loadingDefaultsId === currentEmployeeId;
              const defaultsError = currentEmployeeId != null && defaultsErrorId === currentEmployeeId;
              const hasSignedFile = !!(doc.signed_file_url || doc.file_firmato || doc.has_signed_file);
              const effectiveTiming = doc.notification_timing != null ? doc.notification_timing : defaults?.default_days ?? null;

              const defaultEmail = defaults?.default_days != null
                ? buildDefaultEmailCsv({
                    send_company: !!defaults.send_company,
                    company_email: defaults.company_email || '',
                    send_handlers: !!defaults.send_handlers,
                    handlers_emails: defaults.handlers_emails || [],
                    others_emails: defaults.others_emails || '',
                  })
                : '';

              const effectiveEmail = String(doc.notification_email ?? '').trim() || (effectiveTiming != null ? defaultEmail : '');

              return (
                <React.Fragment key={doc.id}>
                  <tr
                    className={`grid min-w-0 grid-cols-[minmax(0,1fr)_auto] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm xl:table-row xl:overflow-visible xl:rounded-none xl:border-0 xl:shadow-none cursor-pointer transition-colors ${expanded ? 'xl:bg-indigo-50/40' : 'xl:hover:bg-gray-50'}`}
                    onClick={() => toggleDoc(doc.id)}
                  >
                    {/* ESPANDI */}
                    <td className="order-2 flex items-start justify-end px-3 py-3 xl:table-cell xl:px-2 xl:py-3 xl:align-middle">
                      <button
                        type="button"
                        aria-label={expanded ? 'Chiudi dettagli documento' : 'Apri dettagli documento'}
                        aria-expanded={expanded}
                        onClick={e => { e.stopPropagation(); toggleDoc(doc.id); }}
                        className="rounded p-1 text-gray-500 hover:bg-indigo-100 hover:text-indigo-700"
                      >
                        {expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                      </button>
                    </td>

                    {/* NOME */}
                    <td className="order-1 min-w-0 px-3 py-3 text-sm xl:table-cell xl:py-4 xl:pl-4 xl:pr-3 xl:align-middle">
                      <div className="min-w-0 break-words font-medium text-gray-900">{doc.name}</div>
                    </td>

                    {/* DATE */}
                    <td className="order-4 col-span-2 flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 text-sm text-gray-500 xl:table-cell xl:border-0 xl:px-3 xl:py-3.5 xl:align-middle">
                      <span className="text-xs font-medium text-gray-500 xl:hidden">
                        Creato il
                      </span>
                      <span className="break-words">
                        {formatDate(doc.created_at, true)}
                      </span>
                    </td>
                    <td className="order-5 col-span-2 flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 text-sm text-gray-500 xl:table-cell xl:border-0 xl:px-3 xl:py-3.5 xl:align-middle">
                      <span className="text-xs font-medium text-gray-500 xl:hidden">
                        Scadenza
                      </span>
                      <span className="break-words">
                        {formatDate(doc.due_date)}
                      </span>
                    </td>

                    {/* STATO */}
                    <td className="order-3 col-span-2 flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 xl:table-cell xl:border-0 xl:px-3 xl:py-3.5 xl:align-middle">
                      <span className="text-xs font-medium text-gray-500 xl:hidden">
                        Stato
                      </span>
                      {renderDocumentStatus(doc)}
                    </td>

                    {/* AZIONI */}
                    <td className="order-6 col-span-2 min-w-0 border-t border-gray-100 px-3 py-3 xl:table-cell xl:border-0 xl:py-3.5 xl:pl-3 xl:pr-4 xl:align-middle">
                      <div
                        onClick={e => e.stopPropagation()}
                        className="flex min-w-0 flex-wrap items-center gap-2 xl:justify-end"
                      >
                        <span className="mr-auto text-xs font-medium text-gray-500 xl:hidden">
                          Azioni
                        </span>

                        {showDeadlineEdit && (
                          <CustomTooltipComponent title="Modifica scadenza e promemoria">
                            <button type="button" disabled={busy} onClick={() => onEditDeadline(doc)} className={actionButtonClass}>
                              <PencilSquareIcon className="h-5 w-5" />
                            </button>
                          </CustomTooltipComponent>
                        )}
                        {hasSignedFile ? (
                          <CustomTooltipComponent title="Visualizza firmato">
                            <button type="button" disabled={busy} onClick={() => openFile(doc, 'signed')} className={actionButtonClass}>
                              <DocumentCheckIcon className="h-5 w-5" />
                            </button>
                          </CustomTooltipComponent>
                        ) : (
                          <CustomTooltipComponent title="Visualizza originale">
                            <button type="button" disabled={busy} onClick={() => openFile(doc, 'original')} className={actionButtonClass}>
                              <EyeIcon className="h-5 w-5" />
                            </button>
                          </CustomTooltipComponent>
                        )}
                        <CustomTooltipComponent title="Firmatari">
                          <button type="button" disabled={busy} onClick={() => setSignersDoc(doc)} className={actionButtonClass}>
                            <UsersIcon className="h-5 w-5" />
                          </button>
                        </CustomTooltipComponent>
                        {!doc.is_signed && !['ERROR', 'REJECTED'].includes(doc.status) && isSentToProvider(doc) && (
                          <CustomTooltipComponent title="Reinvia email">
                            <button type="button" disabled={busy} onClick={() => resend(doc)} className={actionButtonClass}>
                              <PaperAirplaneIcon className="h-5 w-5" />
                            </button>
                          </CustomTooltipComponent>
                        )}
                        {isPending && (
                          <CustomTooltipComponent title="Aggiorna stato">
                            <button type="button" disabled={busy} onClick={() => sync(doc)} className={actionButtonClass}>
                              <ArrowPathIcon className="h-5 w-5" />
                            </button>
                          </CustomTooltipComponent>
                        )}
                        <CustomTooltipComponent title="Elimina">
                          <button type="button" disabled={busy} onClick={() => remove(doc)} className={dangerButtonClass}>
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </CustomTooltipComponent>
                      </div>
                    </td>
                  </tr>

                  {/* DETTAGLIO ESPANDIBILE */}
                  {expanded && (
                    <tr className="block min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 xl:table-row xl:overflow-visible xl:rounded-none xl:border-0">
                      <td colSpan={columnCount} className="block min-w-0 bg-gray-50 xl:table-cell xl:border-t xl:border-gray-100">
                        <div className="px-4 py-4 text-sm text-gray-700 sm:px-6">
                          <dl className="divide-y divide-gray-200">
                            <DetailField label="Dipendenti associati al documento">
                              <div className="divide-y divide-gray-200">
                                {(doc.employees || []).map(employee => {
                                  const state = employee.signature_status;
                                  const signed = state === 'SIGNED' || state === 'DONE';
                                  const error = state === 'ERROR';
                                  const pending = state === 'PENDING' || state === 'READYTOSIGN';

                                  return (
                                    <div key={employee.id} className="grid grid-cols-1 gap-2 py-3 sm:grid-cols-12 sm:items-center sm:gap-4">
                                      <div className="sm:col-span-5">
                                        <div className="text-sm font-semibold uppercase text-gray-800">
                                          {[employee.last_name, employee.first_name].filter(Boolean).join(' ') || employee.cf}
                                        </div>
                                        <div className="text-xs text-gray-500">{employee.cf || '—'}</div>
                                      </div>

                                      <div className="sm:col-span-3">
                                        {signed ? (
                                          <span className="inline-flex rounded-full border border-green-500 bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                                            Ha firmato
                                          </span>
                                        ) : error ? (
                                          <span className="inline-flex rounded-full border border-red-500 bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
                                            Errore firma
                                          </span>
                                        ) : pending ? (
                                          <span className="inline-flex rounded-full border border-yellow-600 bg-yellow-100 px-2.5 py-1 text-xs font-semibold text-yellow-800">
                                            Da firmare
                                          </span>
                                        ) : (
                                          <span className="text-xs text-gray-500">Stato non disponibile</span>
                                        )}
                                      </div>

                                      {(employee.signed_at_display || employee.signed_at) && (
                                        <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600 sm:col-span-4">
                                          <span className="text-xs text-gray-500">OTP confermato il</span>
                                          <span className="font-semibold text-green-700">
                                            {employee.signed_at_display || formatDate(employee.signed_at, true)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </DetailField>
                            <DetailField label="Descrizione">{doc.description || '—'}</DetailField>
                            <DetailField label="Data caricamento">{formatDate(doc.created_at, true)}</DetailField>
                            <DetailField label="Ultimo aggiornamento">{formatDate(doc.updated_at, true)}</DetailField>
                            <DetailField label="Data scadenza">{formatDate(doc.due_date)}</DetailField>
                            <DetailField label="Stato documento">
                              {renderDocumentStatus(doc)}
                            </DetailField>
                            {['ERROR', 'REJECTED'].includes(doc.status) && (
                              <DetailField label="Errore firma">
                                <div className="text-red-700">
                                  <p className="font-medium">
                                    {doc.status === 'REJECTED'
                                      ? 'La firma è stata rifiutata.'
                                      : 'La procedura di firma non è riuscita.'}
                                  </p>

                                  {doc.provider_error_message && (
                                    <p className="mt-1 text-sm">
                                      {doc.provider_error_message}
                                    </p>
                                  )}

                                  {doc.provider_error_code != null && (
                                    <p className="mt-1 text-xs text-gray-500">
                                      Codice errore: {doc.provider_error_code}
                                    </p>
                                  )}
                                </div>
                              </DetailField>
                            )}
                            <DetailField label="Tipo di firma">{doc.signature_service || '—'}</DetailField>
                            <DetailField label="Provider firma">{doc.provider_name || '—'}</DetailField>
                            <DetailField label="Nome file"><span className="break-all">{doc.file_name || '—'}</span></DetailField>
                            {doc.file_size != null && (
                              <DetailField label="Dimensione file">{formatFileSize(doc.file_size)}</DetailField>
                            )}
                            {doc.due_date && (
                              <>
                                <DetailField label="Promemoria scadenza">
                                  {loadingDefaults
                                    ? 'Caricamento...'
                                    : defaultsError
                                      ? 'Impossibile recuperare le impostazioni predefinite'
                                      : effectiveTiming != null
                                        ? `${effectiveTiming} giorni`
                                        : defaults || doc.notification_timing != null
                                          ? 'Nessun promemoria'
                                          : 'Impostazioni non disponibili'}
                                </DetailField>
                                <DetailField label="Email di notifica scadenza">
                                  {loadingDefaults
                                    ? 'Caricamento...'
                                    : defaultsError
                                      ? 'Impossibile recuperare i destinatari predefiniti'
                                      : effectiveTiming == null
                                        ? 'Nessun promemoria attivo'
                                        : effectiveEmail
                                          ? formatEmails(effectiveEmail)
                                          : 'Nessun destinatario configurato'}
                                </DetailField>
                              </>
                            )}
                            <DetailField label="Visualizza file">
                              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                {hasSignedFile ? (
                                  <button type="button" disabled={busy} onClick={() => openFile(doc, 'signed')} className="inline-flex items-center gap-1 text-indigo-600 hover:underline disabled:opacity-40">
                                    <DocumentCheckIcon className="h-4 w-4" /> Firmato
                                  </button>
                                ) : (
                                  <button type="button" disabled={busy} onClick={() => openFile(doc, 'original')} className="inline-flex items-center gap-1 text-indigo-600 hover:underline disabled:opacity-40">
                                    <EyeIcon className="h-4 w-4" /> Originale
                                  </button>
                                )}
                              </div>
                            </DetailField>
                            {showDeadlineEdit && (
                              <DetailField label="Azioni">
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => onEditDeadline(doc)}
                                  className="inline-flex items-center gap-1 text-indigo-600 hover:underline disabled:opacity-40"
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                  Modifica scadenza e promemoria
                                </button>
                              </DetailField>
                            )}
                            {doc?.acceptance_files?.length > 0 && (
                              <DetailField label="Documenti di firma">
                                <div className="flex flex-col gap-2">
                                  {doc.acceptance_files.map((item) => (
                                    <div
                                      key={item.id}
                                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2"
                                    >
                                      <span className="text-sm text-gray-700">
                                        {item.kind === 'audit'
                                          ? 'Audit della firma'
                                          : 'Documento di accettazione'}
                                      </span>

                                      <CustomTooltipComponent title="Visualizza PDF">
                                        <button
                                          type="button"
                                          disabled={busy || !item.file_url}
                                          onClick={() => openFile(doc, item.kind, item)}
                                          className={actionButtonClass}
                                          aria-label={
                                            item.kind === 'audit'
                                              ? 'Visualizza audit della firma'
                                              : 'Visualizza documento di accettazione'
                                          }
                                        >
                                          <EyeIcon className="h-5 w-5" />
                                        </button>
                                      </CustomTooltipComponent>
                                    </div>
                                  ))}
                                </div>
                              </DetailField>
                            )}
                          </dl>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ANTEPRIMA */}
      <FilePreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />

      {/* DETTAGLIO FIRMATARI */}
      <FirmeSignersDialog
        key={signersDoc?.id ?? 'closed'}
        doc={signersDoc}
        onClose={() => setSignersDoc(null)}
      />
    </>
  );
}
